import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import * as i1 from '@ngrx/store';
import { createAction, props } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import * as i1$2 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import { IconNotificationComponent, IconNotificationModule } from '@psu/components/notification';
import { map, tap } from 'rxjs/operators';
import * as i1$1 from '@angular/material/snack-bar';
import { MatSnackBarConfig } from '@angular/material/snack-bar';

const PREFIX = '[@psu/components Notification]';
const errorNotification = createAction(`${PREFIX} Error`, props());
const warningNotification = createAction(`${PREFIX} Warning`, props());
const infoNotification = createAction(`${PREFIX} Info`, props());
const successNotification = createAction(`${PREFIX} Success`, props());
const generalNotification = createAction(`${PREFIX} General`, props());
const fromComponentNotification = createAction(`${PREFIX} From Component`, props());
const dismissAllNotifications = createAction(`${PREFIX} Dismiss All`);

class NotificationFacade {
    constructor(store) {
        this.store = store;
    }
    fromComponent(component, notification) {
        this.store.dispatch(fromComponentNotification({ component, notification }));
    }
    general(notification) {
        this.store.dispatch(generalNotification({ notification }));
    }
    error(notification) {
        this.store.dispatch(errorNotification({ notification }));
    }
    warning(notification) {
        this.store.dispatch(warningNotification({ notification }));
    }
    info(notification) {
        this.store.dispatch(infoNotification({ notification }));
    }
    success(notification) {
        this.store.dispatch(successNotification({ notification }));
    }
    dismissAll() {
        this.store.dispatch(dismissAllNotifications());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationFacade, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationFacade, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationFacade, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class NotificationService {
    constructor(snackBar) {
        this.snackBar = snackBar;
    }
    /**
     * Show a component notification.
     *
     * @param component the component to display in the notification
     * @param message the notification message
     * @param closeText optional close text, if not provided, there is no close text.
     * @param duration optional duration in milliseconds, defaults to 5 seconds
     */
    fromComponent(component, notification) {
        const not = {
            duration: 5000,
            ...notification,
        };
        return this.show(not, '', component);
    }
    /**
     * Show a general notification.
     */
    general(notification) {
        const not = {
            duration: 5000,
            ...notification,
        };
        return this.show(not, '', not.icon ? IconNotificationComponent : undefined);
    }
    /**
     * Show an error notification.
     */
    error(notification) {
        const not = {
            closeText: 'Close',
            ...notification,
        };
        return this.show(not, 'error', not.icon ? IconNotificationComponent : undefined);
    }
    /**
     * Show a warning notification.
     */
    warn(notification) {
        const not = {
            duration: 5000,
            ...notification,
        };
        return this.show(not, 'warn', not.icon ? IconNotificationComponent : undefined);
    }
    /**
     * Show an info notification.
     */
    info(notification) {
        const not = {
            duration: 5000,
            ...notification,
        };
        return this.show(not, 'info', not.icon ? IconNotificationComponent : undefined);
    }
    /**
     * Show a success notification.
     */
    success(notification) {
        const not = {
            duration: 5000,
            ...notification,
        };
        return this.show(not, 'success', not.icon ? IconNotificationComponent : undefined);
    }
    dismiss() {
        if (this.current) {
            this.current.dismiss();
        }
    }
    show(notification, className, component) {
        const config = new MatSnackBarConfig();
        config.duration = notification.duration;
        config.panelClass = ['uic-snack-bar-container', className].filter((c) => !!c);
        config.data = component
            ? { action: notification.closeText, message: notification.message, icon: notification.icon }
            : null;
        this.current = component
            ? this.snackBar.openFromComponent(component, config)
            : this.snackBar.open(notification.message, notification.closeText, config);
        return this.current;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationService, deps: [{ token: i1$1.MatSnackBar }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$1.MatSnackBar }]; } });

class NotificationEffects {
    constructor(actions$, service) {
        this.actions$ = actions$;
        this.service = service;
        this.generalNotification$ = createEffect(() => this.actions$.pipe(ofType(generalNotification), map((action) => action.notification), tap((notification) => this.service.general(notification))), { dispatch: false });
        this.fromComponentNotification$ = createEffect(() => this.actions$.pipe(ofType(fromComponentNotification), tap((action) => this.service.fromComponent(action.component, action.notification))), { dispatch: false });
        this.errorNotification$ = createEffect(() => this.actions$.pipe(ofType(errorNotification), map((action) => action.notification), tap((notification) => this.service.error(notification)), map(() => this.noAction())), { dispatch: false });
        this.warningNotification$ = createEffect(() => this.actions$.pipe(ofType(warningNotification), map((action) => action.notification), tap((notification) => this.service.warn(notification)), map(() => this.noAction())), { dispatch: false });
        this.infoNotification$ = createEffect(() => this.actions$.pipe(ofType(infoNotification), map((action) => action.notification), tap((notification) => this.service.info(notification)), map(() => this.noAction())), { dispatch: false });
        this.successNotification$ = createEffect(() => this.actions$.pipe(ofType(successNotification), map((action) => action.notification), tap((notification) => this.service.success(notification)), map(() => this.noAction())), { dispatch: false });
        this.dismissAllNotifications$ = createEffect(() => this.actions$.pipe(ofType(dismissAllNotifications), tap(() => this.service.dismiss()), map(() => this.noAction())), { dispatch: false });
    }
    noAction() {
        return {
            type: 'NO_ACTION',
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationEffects, deps: [{ token: i1$2.Actions }, { token: NotificationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationEffects, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: NotificationService }]; } });

class NotificationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: NotificationModule, imports: [CommonModule, IconNotificationModule, i1$2.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationModule, imports: [CommonModule, IconNotificationModule, EffectsModule.forFeature([NotificationEffects])] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NotificationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, IconNotificationModule, EffectsModule.forFeature([NotificationEffects])],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { NotificationFacade, NotificationModule, NotificationService };
